/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-confusing-void-expression */
import React from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { Button, Box, Typography, Container } from '@mui/material';
import ReportIcon from '@mui/icons-material/Report';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { colorTheme } from 'theme';
import { useTheme } from '@emotion/react';

const isNetworkError = (error: Error): boolean => {
  return (
    error.message.includes('Network Error') ||
    error.message.includes('Failed to fetch') ||
    error.message.includes('ERR_INTERNET_DISCONNECTED') ||
    error.message.includes('ECONNREFUSED') ||
    error.message.includes('ECONNRESET') ||
    error.message.includes('ENOTFOUND')
  );
};

const ErrorFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) => {
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);

  const isNetworkIssue = isNetworkError(error);

  return (
    <Container
      maxWidth="md"
      style={{
        border: '2px solid',
        borderColor: colors.greenAccent[500],
        borderRadius: 10,
        margin: 'auto',
        padding: 20,
        background: isNetworkIssue ? '#ffebee' : '#e3f2fd', // Red for network issues, Blue for general errors
      }}
    >
      <Box
        style={{ textAlign: 'center' }}
        className="flex flex-col items-center justify-center h-screen text-center"
      >
        {isNetworkIssue ? (
          <WifiOffIcon style={{ fontSize: 100, color: '#d32f2f' }} />
        ) : (
          <ReportIcon
            style={{ fontSize: 100, color: colors.greenAccent[500] }}
          />
        )}

        <Typography variant="h3" color="error" fontWeight="bold" sx={{ mt: 2 }}>
          {isNetworkIssue
            ? 'No Internet Connection!'
            : 'Oops! Something went wrong.'}
        </Typography>

        <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
          {isNetworkIssue
            ? "It looks like you're offline. Please check your internet connection and try again."
            : 'An unexpected error occurred. Please try again later.'}
        </Typography>

        <Button
          onClick={resetErrorBoundary}
          variant="contained"
          color="primary"
          sx={{
            bgcolor: colors.greenAccent[500],
            borderRadius: 2,
            textTransform: 'none',
            mt: 2,
          }}
        >
          {isNetworkIssue ? 'Retry' : 'Try Again'}
        </Button>
      </Box>
    </Container>
  );
};

const ErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => window.location.reload()}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
